import React from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { Icons } from '../../../shared/icons';
import ClassificationSelector from './ClassificationSelector';

export default function ApplianceStep({ 
  form, 
  setNestedField,
  addItem,
  removeItem,
  certificateType,
  errors = {}
}) {
  const appliances = form.appliances || [];

  const applianceTypes = [
    { value: 'boiler', label: 'Boiler' },
    { value: 'fire', label: 'Gas Fire' },
    { value: 'cooker', label: 'Cooker' },
    { value: 'hob', label: 'Hob' },
    { value: 'water_heater', label: 'Water Heater' },
    { value: 'gas_fire', label: 'Gas Fire' },
    { value: 'other', label: 'Other' }
  ];

  const flueTypes = [
    { value: 'room_sealed', label: 'Room Sealed' },
    { value: 'open_flue', label: 'Open Flue' },
    { value: 'flueless', label: 'Flueless' },
    { value: 'balanced_flue', label: 'Balanced Flue' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="appliance-step">
      <div className="panel appliance-panel">
        <h3>Appliances Checked</h3>
        <p className="field-hint">Record all gas appliances and flues at the property</p>
      </div>

      {appliances.map((appliance, index) => (
        <div key={index} className="appliance-item cp12-section">
          <div className="cp12-section-header">
            <h4>Appliance {index + 1}</h4>
            {appliances.length > 1 && (
              <button
                type="button"
                className="cp12-remove"
                onClick={() => removeItem('appliances', index)}
                aria-label={`Remove appliance ${index + 1}`}
              >
                <SafeIcon icon={Icons.X} size={16} />
                Remove
              </button>
            )}
          </div>

          <div className="form-row">
            <label className="cert-field">
              Type
              <select
                value={appliance.type}
                onChange={(e) => setNestedField(`appliances[${index}].type`, e.target.value)}
                aria-label={`Appliance ${index + 1} type`}
              >
                <option value="">Select type...</option>
                {applianceTypes.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <label className="cert-field">
              Make
              <input
                type="text"
                value={appliance.make}
                onChange={(e) => setNestedField(`appliances[${index}].make`, e.target.value)}
                placeholder="e.g. Worcester"
                aria-label={`Appliance ${index + 1} make`}
              />
            </label>

            <label className="cert-field">
              Model
              <input
                type="text"
                value={appliance.model}
                onChange={(e) => setNestedField(`appliances[${index}].model`, e.target.value)}
                placeholder="e.g. Greenstar 30i"
                aria-label={`Appliance ${index + 1} model`}
              />
            </label>
          </div>

          <label className="cert-field">
            Location
            <input
              type="text"
              value={appliance.location}
              onChange={(e) => setNestedField(`appliances[${index}].location`, e.target.value)}
              placeholder="e.g. Kitchen / Airing cupboard"
              aria-label={`Appliance ${index + 1} location`}
            />
          </label>

          <div className="form-row">
            <label className="cert-field">
              Flue Type
              <select
                value={appliance.flue_type}
                onChange={(e) => setNestedField(`appliances[${index}].flue_type`, e.target.value)}
                aria-label={`Appliance ${index + 1} flue type`}
              >
                <option value="">Select flue type...</option>
                {flueTypes.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <label className="cert-field">
              Flue Location
              <input
                type="text"
                value={appliance.flue_location}
                onChange={(e) => setNestedField(`appliances[${index}].flue_location`, e.target.value)}
                placeholder="e.g. Roof / Wall"
                aria-label={`Appliance ${index + 1} flue location`}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="cert-field">
              Heat Input (kW)
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={appliance.heat_input_kw}
                onChange={(e) => setNestedField(`appliances[${index}].heat_input_kw`, e.target.value)}
                placeholder="e.g. 24.5"
                aria-label={`Appliance ${index + 1} heat input`}
              />
            </label>

            <label className="cert-field">
              Heat Output (kW)
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={appliance.heat_output_kw}
                onChange={(e) => setNestedField(`appliances[${index}].heat_output_kw`, e.target.value)}
                placeholder="e.g. 22.0"
                aria-label={`Appliance ${index + 1} heat output`}
              />
            </label>
          </div>

          <label className="cert-field">
            Safe to Use
            <div className="toggle-group">
              <button
                type="button"
                className={`toggle-option ${appliance.safe_to_use === 'yes' ? 'selected' : ''}`}
                onClick={() => setNestedField(`appliances[${index}].safe_to_use`, 'yes')}
                aria-label={`Mark appliance ${index + 1} as safe to use`}
                aria-pressed={appliance.safe_to_use === 'yes'}
              >
                <SafeIcon icon={Icons.Check} size={20} />
                YES
              </button>
              <button
                type="button"
                className={`toggle-option ${appliance.safe_to_use === 'no' ? 'selected' : ''}`}
                onClick={() => setNestedField(`appliances[${index}].safe_to_use`, 'no')}
                aria-label={`Mark appliance ${index + 1} as not safe to use`}
                aria-pressed={appliance.safe_to_use === 'no'}
              >
                <SafeIcon icon={Icons.X} size={20} />
                NO
              </button>
              <button
                type="button"
                className={`toggle-option ${appliance.safe_to_use === 'na' ? 'selected' : ''}`}
                onClick={() => setNestedField(`appliances[${index}].safe_to_use`, 'na')}
                aria-label={`Mark appliance ${index + 1} as not applicable`}
                aria-pressed={appliance.safe_to_use === 'na'}
              >
                N/A
              </button>
            </div>
          </label>

          <label className="cert-field">
            Defects Found
            <textarea
              value={appliance.defects}
              onChange={(e) => setNestedField(`appliances[${index}].defects`, e.target.value)}
              placeholder="Describe any defects found"
              rows={2}
              aria-label={`Appliance ${index + 1} defects`}
            />
          </label>

          <label className="cert-field">
            Remedial Action
            <textarea
              value={appliance.remedial_action}
              onChange={(e) => setNestedField(`appliances[${index}].remedial_action`, e.target.value)}
              placeholder="Action taken or required"
              rows={2}
              aria-label={`Appliance ${index + 1} remedial action`}
            />
          </label>

          <label className="cert-field">
            Inspector Comments
            <textarea
              value={appliance.inspector_comments}
              onChange={(e) => setNestedField(`appliances[${index}].inspector_comments`, e.target.value)}
              placeholder="Additional comments"
              rows={2}
              aria-label={`Appliance ${index + 1} inspector comments`}
            />
          </label>
        </div>
      ))}

      <button
        type="button"
        className="secondary-button add-appliance-button"
        onClick={() => addItem('appliances', {
          type: '',
          make: '',
          model: '',
          location: '',
          flue_type: '',
          flue_location: '',
          heat_input_kw: '',
          heat_output_kw: '',
          safe_to_use: '',
          defects: '',
          remedial_action: '',
          inspector_comments: ''
        })}
        aria-label="Add new appliance"
      >
        <SafeIcon icon={Icons.Plus} size={18} />
        Add Appliance
      </button>

      {errors.appliances && (
        <div className="validation-errors">
          <span className="error-message">{errors.appliances}</span>
        </div>
      )}
    </div>
  );
}